import * as React from 'react';

import {
  Badge,
  Button,
  Col,
  ContentSection,
  LocalizedDate,
  Row,
  Select,
  TextField,
} from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';

import { $cameraForm, CameraFormState } from '../../../_state/cameraForm.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { CameraDetailRecord } from '../../../types/sidecar';

type CameraFormFieldsProps = {
  camera?: CameraDetailRecord | null;
  className?: string;
  phase?: string | null;
  hasCameraClient?: boolean;
  rtspEnabled?: boolean | null;
  rtspPort?: number | null;
  httpEnabled?: boolean | null;
  httpPort?: number | null;
  onvifEnabled?: boolean | null;
  onvifPort?: number | null;
  onEnableRtsp?: () => void;
  onEnableHttp?: () => void;
  onEnableOnvif?: () => void;
  isEnablingRtsp?: boolean;
  isEnablingHttp?: boolean;
  isEnablingOnvif?: boolean;
};

const cameraFormSelector = (state: CameraFormState) => ({
  name: state.name,
  streamProfile: state.streamProfile,
  streamUrl: state.streamUrl,
  snapshotUrl: state.snapshotUrl,
  username: state.username,
  password: state.password,
});

export const CameraFormFields = ({
  camera,
  className,
  phase,
  hasCameraClient,
  rtspEnabled,
  rtspPort,
  httpEnabled,
  httpPort,
  onvifEnabled,
  onvifPort,
  onEnableRtsp,
  onEnableHttp,
  onEnableOnvif,
  isEnablingRtsp,
  isEnablingHttp,
  isEnablingOnvif,
}: CameraFormFieldsProps) => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();

  const selectedFormAtom = React.useMemo(
    () => selectAtom($cameraForm, cameraFormSelector, isEqual),
    [],
  );

  const form = useAtomValue(selectedFormAtom);
  const updateForm = useSetAtom($cameraForm);

  const handleFieldChange = React.useCallback(
    <K extends keyof CameraFormState>(field: K) =>
      (value: CameraFormState[K]) => {
        updateForm((prev) => ({
          ...prev,
          [field]: value,
        }));
      },
    [updateForm],
  );

  const isRtsp = camera?.driverType === 'rtsp';

  type DetailFieldProps = {
    label: string;
    value: React.ReactNode;
  };

  const DetailField = ({ label, value }: DetailFieldProps) => (
    <Row className="@DetailField items-start gap-4 py-2">
      <div className="w-[180px] shrink-0 text-sm text-neutral/60">{label}</div>
      <div className="text-sm">{value ?? '\u2013'}</div>
    </Row>
  );

  const streamProfileOptions = React.useMemo(
    () => [
      {
        key: 'sub',
        label: t.hardwareView.cameraDetailView.configuration.profileSub,
      },
      {
        key: 'main',
        label: t.hardwareView.cameraDetailView.configuration.profileMain,
      },
    ],
    [t],
  );

  return (
    <div className={cn('@CameraFormFields', className)}>
      <ContentSection
        title={t.hardwareView.cameraDetailView.sections.configuration}
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <TextField
            label={t.hardwareView.cameraDetailView.fields.name}
            value={form.name}
            onChange={(value) => handleFieldChange('name')(value)}
            isRequired
          />
          {!isRtsp && (
            <Select
              label={t.hardwareView.cameraDetailView.fields.streamProfile}
              options={streamProfileOptions}
              value={form.streamProfile}
              onChange={(v) => v && handleFieldChange('streamProfile')(v)}
            />
          )}
          {isRtsp && (
            <>
              <TextField
                label={t.hardwareView.cameraDetailView.fields.streamUrl}
                value={form.streamUrl}
                onChange={(value) => handleFieldChange('streamUrl')(value)}
              />
              <TextField
                label={t.hardwareView.cameraDetailView.fields.snapshotUrl}
                value={form.snapshotUrl}
                onChange={(value) => handleFieldChange('snapshotUrl')(value)}
              />
            </>
          )}
        </div>
      </ContentSection>

      {!isRtsp && (
        <ContentSection
          title={t.hardwareView.cameraDetailView.sections.credentials}
          variant="panel"
        >
          {phase === 'wrong_credentials' && (
            <div className="mb-4 rounded-md border border-failure/30 bg-failure/5 px-4 py-3 text-sm text-failure">
              {t.hardwareView.cameraDetailView.messages.wrongCredentialsWarning}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <TextField
              label={t.hardwareView.cameraDetailView.fields.username}
              value={form.username}
              onChange={(value) => handleFieldChange('username')(value)}
            />
            <TextField
              label={t.hardwareView.cameraDetailView.fields.password}
              value={form.password}
              onChange={(value) => handleFieldChange('password')(value)}
              type="password"
            />
          </div>
        </ContentSection>
      )}

      <ContentSection
        title={t.hardwareView.cameraDetailView.sections.deviceInfo}
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.cameraDetailView.fields.uniqueId}
            </span>
            <span className="font-mono text-sm">
              {camera?.uniqueId ?? '\u2013'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.cameraDetailView.fields.driverType}
            </span>
            <span className="text-sm font-medium">
              {camera?.driverType ?? '\u2013'}
            </span>
          </div>
          {!isRtsp && (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral/60">
                  {t.hardwareView.cameraDetailView.fields.manufacturer}
                </span>
                <span className="text-sm font-medium">
                  {camera?.manufacturer ?? '\u2013'}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral/60">
                  {t.hardwareView.cameraDetailView.fields.model}
                </span>
                <span className="text-sm font-medium">
                  {camera?.model ?? '\u2013'}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral/60">
                  {t.hardwareView.cameraDetailView.fields.firmwareVersion}
                </span>
                <span className="text-sm font-medium">
                  {camera?.firmwareVersion ?? '\u2013'}
                </span>
              </div>
            </>
          )}
        </div>
      </ContentSection>

      <ContentSection
        title={t.hardwareView.cameraDetailView.sections.network}
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-2">
          {!isRtsp && camera?.ipAddress && (
            <div className="flex flex-col gap-1">
              <span className="text-sm text-neutral/60">
                {t.hardwareView.cameraDetailView.fields.ipAddress}
              </span>
              <span className="font-mono text-sm">{camera.ipAddress}</span>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.cameraDetailView.fields.status}
            </span>
            <span className="text-sm font-medium">
              {camera
                ? t.hardwareView.camerasPanel.statusLabels[camera.status]
                : '\u2013'}
            </span>
          </div>
          {!isRtsp && (
            <div className="flex flex-col gap-1">
              <span className="text-sm text-neutral/60">
                {t.hardwareView.cameraDetailView.fields.lastSeenAt}
              </span>
              <span className="text-sm font-medium">
                {camera?.lastSeenAt ? (
                  <LocalizedDate date={camera.lastSeenAt} />
                ) : (
                  '\u2013'
                )}
              </span>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.cameraDetailView.fields.sidecar}
            </span>
            {camera?.sidecar ? (
              <button
                className="w-fit text-sm font-medium text-primary hover:underline"
                onClick={() =>
                  setLocation(`/hardware/sidecars/${camera.sidecar?.id}`)
                }
              >
                {camera.sidecar.name}
              </button>
            ) : (
              <span className="text-sm font-medium">{'\u2013'}</span>
            )}
          </div>
        </div>
      </ContentSection>

      {!isRtsp && (
        <ContentSection
          title={t.hardwareView.cameraDetailView.sections.setup}
          variant="panel"
        >
          {phase === 'provisioning' && (
            <div className="mb-4 flex items-center gap-2">
              {httpEnabled === false && (
                <Button
                  variant="outline"
                  size="small"
                  onPress={onEnableHttp}
                  isDisabled={isEnablingHttp || !hasCameraClient}
                >
                  {isEnablingHttp
                    ? t.hardwareView.cameraDetailView.actions.enablingHttp
                    : t.hardwareView.cameraDetailView.actions.enableHttp}
                </Button>
              )}
              {rtspEnabled === false && (
                <Button
                  variant="outline"
                  size="small"
                  onPress={onEnableRtsp}
                  isDisabled={isEnablingRtsp || !hasCameraClient}
                >
                  {isEnablingRtsp
                    ? t.hardwareView.cameraDetailView.actions.enablingRtsp
                    : t.hardwareView.cameraDetailView.actions.enableRtsp}
                </Button>
              )}
              {onvifEnabled === false && (
                <Button
                  variant="outline"
                  size="small"
                  onPress={onEnableOnvif}
                  isDisabled={isEnablingOnvif || !hasCameraClient}
                >
                  {isEnablingOnvif
                    ? t.hardwareView.cameraDetailView.actions.enablingOnvif
                    : t.hardwareView.cameraDetailView.actions.enableOnvif}
                </Button>
              )}
            </div>
          )}
          <Col>
            <DetailField
              label={t.hardwareView.cameraDetailView.fields.httpStatus}
              value={
                httpEnabled != null ? (
                  <Badge color={httpEnabled ? 'success' : 'neutral'}>
                    {httpEnabled
                      ? t.hardwareView.cameraDetailView.httpStatus.enabled
                      : t.hardwareView.cameraDetailView.httpStatus.disabled}
                  </Badge>
                ) : (
                  '\u2013'
                )
              }
            />
            {httpEnabled && httpPort != null && (
              <DetailField
                label={t.hardwareView.cameraDetailView.fields.httpPort}
                value={<span className="font-mono text-sm">{httpPort}</span>}
              />
            )}
            <DetailField
              label={t.hardwareView.cameraDetailView.fields.rtspStatus}
              value={
                rtspEnabled != null ? (
                  <Badge color={rtspEnabled ? 'success' : 'neutral'}>
                    {rtspEnabled
                      ? t.hardwareView.cameraDetailView.rtspStatus.enabled
                      : t.hardwareView.cameraDetailView.rtspStatus.disabled}
                  </Badge>
                ) : (
                  '\u2013'
                )
              }
            />
            {rtspEnabled && rtspPort != null && (
              <DetailField
                label={t.hardwareView.cameraDetailView.fields.rtspPort}
                value={<span className="font-mono text-sm">{rtspPort}</span>}
              />
            )}
            <DetailField
              label={t.hardwareView.cameraDetailView.fields.onvifStatus}
              value={
                onvifEnabled != null ? (
                  <Badge color={onvifEnabled ? 'success' : 'neutral'}>
                    {onvifEnabled
                      ? t.hardwareView.cameraDetailView.onvifStatus.enabled
                      : t.hardwareView.cameraDetailView.onvifStatus.disabled}
                  </Badge>
                ) : (
                  '\u2013'
                )
              }
            />
            {onvifEnabled && onvifPort != null && (
              <DetailField
                label={t.hardwareView.cameraDetailView.fields.onvifPort}
                value={<span className="font-mono text-sm">{onvifPort}</span>}
              />
            )}
          </Col>
        </ContentSection>
      )}

      <ContentSection
        title={t.hardwareView.cameraDetailView.sections.timestamps}
        variant="panel"
        hideDivider
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.cameraDetailView.fields.created}
            </span>
            <span className="text-sm font-medium">
              {camera?.timestamps.createdAt ? (
                <LocalizedDate date={camera.timestamps.createdAt} />
              ) : (
                '-'
              )}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.cameraDetailView.fields.updated}
            </span>
            <span className="text-sm font-medium">
              {camera?.timestamps.updatedAt ? (
                <LocalizedDate date={camera.timestamps.updatedAt} />
              ) : (
                '-'
              )}
            </span>
          </div>
        </div>
      </ContentSection>
    </div>
  );
};
